import { pickedFile } from '../../../utils/pickedFile.js'
import MediaCaptureField from '../../common/MediaCaptureField.js'
import React, { useState } from 'react';
import SelectField from '../../common/SelectField.js';
import Alert from '../../common/Alert.js';
import gstVerificationPaymentService from '../../../services/GstVerificationPaymentService.js';
import { X, Plus, DollarSign } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js';
import Button from '../../common/Button.js';


const AddPaymentModal = ({ companyId, dueAmount = 0, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_status: 'completed',
    payment_date: new Date().toISOString().split('T')[0],
    transaction_id: '',
    bank_name: '',
    cheque_number: '',
    upi_id: '',
    remarks: ''
  });

  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
    bank_name:    (v) => (paymentData.payment_method === 'bank_transfer' && v) ? validators.textOnly(v, 'Bank name') : '',
    cheque_number:(v) => (paymentData.payment_method === 'cheque' && v) ? validators.alphanumeric(v, 'Cheque number') : '',
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setPaymentData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleReceiptChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt file must be less than 5MB');
        return;
      }
      setReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReceipt(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input-modal');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(paymentData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    if (dueAmount > 0 && parseFloat(paymentData.amount) > dueAmount) {
      Alert.error(`Payment amount ₹${parseFloat(paymentData.amount).toFixed(2)} exceeds the balance due of ₹${dueAmount.toFixed(2)}`);
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        company_id: parseInt(companyId),
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        payment_date: paymentData.payment_date,
        transaction_id: paymentData.transaction_id || null,
        bank_name: paymentData.bank_name || null,
        cheque_number: paymentData.cheque_number || null,
        upi_id: paymentData.upi_id || null,
        remarks: paymentData.remarks || null
      };

      // Try full payment creation first, fallback to quick payment
      try {
        await gstVerificationPaymentService.createPayment(dataToSend, receipt);
      } catch (err) {
        // Fallback to quick payment
        await gstVerificationPaymentService.quickPayment(
          companyId,
          parseFloat(paymentData.amount),
          paymentData.payment_method,
          paymentData.transaction_id,
          paymentData.remarks
        );
      }

      Alert.success('Payment recorded successfully!');
      onSuccess();
    } catch (err) {
      console.error('Error recording payment:', err);
      Alert.error(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Add Payment</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Amount (₹) *
            </label>
            <input
              type="text"
              name="amount"
              value={paymentData.amount}
              onChange={handleChange}
              required
              placeholder="0.00"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.amount ? 'border-red-400' : 'border-gray-300'}`}
            />
            <ValidationMessage error={errors.amount} />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <SelectField variant="form"
              name="payment_method"
              value={paymentData.payment_method}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
              <option value="online">Online</option>
              <option value="other">Other</option>
            </SelectField>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date
            </label>
            <input
              type="date"
              name="payment_date"
              value={paymentData.payment_date}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.payment_date ? 'border-red-400' : 'border-gray-300'}`}
            />
            <ValidationMessage error={errors.payment_date} />
          </div>

          {/* Transaction ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction ID / Reference
            </label>
            <input
              type="text"
              name="transaction_id"
              value={paymentData.transaction_id}
              onChange={handleChange}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Bank Name (if bank transfer) */}
          {paymentData.payment_method === 'bank_transfer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                name="bank_name"
                value={paymentData.bank_name}
                onChange={handleChange}
                placeholder="Bank name"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.bank_name ? 'border-red-400' : 'border-gray-300'}`}
              />
              <ValidationMessage error={errors.bank_name} />
            </div>
          )}

          {/* Cheque Number (if cheque) */}
          {paymentData.payment_method === 'cheque' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cheque Number
              </label>
              <input
                type="text"
                name="cheque_number"
                value={paymentData.cheque_number}
                onChange={handleChange}
                placeholder="Cheque number"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.cheque_number ? 'border-red-400' : 'border-gray-300'}`}
              />
              <ValidationMessage error={errors.cheque_number} />
            </div>
          )}

          {/* UPI ID (if UPI) */}
          {paymentData.payment_method === 'upi' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UPI ID
              </label>
              <input
                type="text"
                name="upi_id"
                value={paymentData.upi_id}
                onChange={handleChange}
                placeholder="UPI ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          )}

          {/* Receipt Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Receipt (Optional)
            </label>
            {!receiptPreview ? (
              <MediaCaptureField mode="document" onPick={handleReceiptChange} />
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg text-sm">
                <span className="text-gray-700 truncate">{receipt?.name}</span>
                <button
                  type="button"
                  onClick={removeReceipt}
                  className="text-red-600 hover:text-red-800 flex-shrink-0"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              name="remarks"
              value={paymentData.remarks}
              onChange={handleChange}
              placeholder="Any payment notes..."
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button variant="primary" type="submit" loading={loading} icon={Plus} className="flex-1">
              {loading ? 'Adding...' : 'Add Payment'}
            </Button>
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentModal;
